import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, withApollo } from 'react-apollo';
import { compose, mapProps } from 'recompose';
import PropTypes from 'prop-types';

import InputField from 'components/common/InputField/InputField';
import { NO_CACHE } from 'graphql/fetchPolicies';
import { getObjValue, truncateDecimals } from 'utils/functions';
import { useDebouncedStartTyping, useDebouncedStopTyping } from 'components/threepp/assessment/Answers/helper';
import {
  updateNumericFieldValue,
  userStartTyping as userStartTypingMut,
  userStopTyping as userStopTypingMut,
  getLatestAssessmentEventById,
  ddeActionsTriggered
} from 'graphql/operations';
import { createNumberValidationFn } from 'utils/validations/validations';
import SaveAnswerHOC from '../SaveAnswerHOC';
import EventHandlerHOC from '../FieldUpdatesHOC';

import './NumericAnswer.scss';

export function NumericAnswer(props) {
  const { save, disabled, question, latestEvent, assessmentId, userStartTyping, userStopTyping } = props;

  const [error, setError] = useState(null);

  const { t } = useTranslation();
  const { numericValue, minValue, maxValue, decimalPlaces, calculated } = question;

  const action = getObjValue(latestEvent, 'action');
  const isDisabled = disabled || action === 'START_TYPING';

  const userStartTypingCallback = useDebouncedStartTyping(userStartTyping, assessmentId, question.id);
  const userStopTypingCallback = useDebouncedStopTyping(userStopTyping, assessmentId, question.id);
  const [inputValue, setInputValue] = useState('');
  const [isUpdated, setIsUpdated] = useState(false);

  const inputValidation = createNumberValidationFn(minValue, maxValue, true);

  const handleChangeCallback = useCallback(
    input => {
      setInputValue(input);
      setIsUpdated(true);
      userStartTypingCallback();
      userStopTypingCallback();
      const normalized = input === '' ? null : input;
      save(normalized);
    },
    [save, userStartTypingCallback, userStopTypingCallback]
  );

  const handleValidationErrorCallback = useCallback(
    e => {
      const normalized = e.value === '' ? null : e.value;
      save(normalized);
    },
    [save]
  );

  useEffect(() => {
    let errorObj = null;
    if (numericValue) {
      const enforcedValue = truncateDecimals(numericValue.toString(), decimalPlaces);
      errorObj = enforcedValue ? inputValidation(enforcedValue.toString()) : null;
    }
    setError(errorObj ? t(errorObj.key, { ...errorObj.args }) : null);
  }, [t, inputValidation, decimalPlaces, numericValue]);

  return (
    <InputField
      t={t}
      type="text" // Use text to avoid input validation issues
      classes="numeric-field-answer"
      name="answer"
      halfWidth
      value={isUpdated && !latestEvent ? inputValue : numericValue}
      disabled={calculated || isDisabled}
      onChange={handleChangeCallback}
      inputValidation={inputValidation}
      maxDecimalsAllowed={decimalPlaces}
      onValidationError={handleValidationErrorCallback}
      error={error}
    />
  );
}

NumericAnswer.defaultProps = {
  latestEvent: { action: '' }
};

NumericAnswer.propTypes = {
  // TODO: Fix this...
  /* eslint-disable react/forbid-prop-types */
  question: PropTypes.object.isRequired,
  save: PropTypes.any.isRequired,
  disabled: PropTypes.bool.isRequired,
  userStartTyping: PropTypes.func.isRequired,
  userStopTyping: PropTypes.func.isRequired,
  latestEvent: PropTypes.object
  /* eslint-enable react/forbid-prop-types */
};

export const NumericAnswerHOC = compose(
  withApollo,
  graphql(updateNumericFieldValue, { name: 'fieldMutation' }),
  graphql(userStartTypingMut, { name: 'userStartTyping' }),
  graphql(userStopTypingMut, { name: 'userStopTyping' }),
  graphql(ddeActionsTriggered, { name: 'ddeActionsTriggered' }),
  graphql(getLatestAssessmentEventById, {
    options: props => ({
      variables: {
        fieldId: props.question.id
      },
      fetchPolicy: NO_CACHE
    }),
    name: 'getLatestAssessmentEventById'
  }),
  EventHandlerHOC,
  SaveAnswerHOC,
  mapProps(props => ({
    assessmentId: props.assessmentId,
    invokeActionsForFields: props.invokeActionsForFields,
    question: props.question,
    disabled: props.disabled,
    save: props.save,
    userStartTyping: props.userStartTyping,
    userStopTyping: props.userStopTyping,
    latestEvent: props.getLatestAssessmentEventById.getLatestAssessmentEventById
  }))
);

export default NumericAnswerHOC(NumericAnswer);
